import { isEmpty } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useListingGroupSellerAccountSettings } from 'src/hooks/api/useListingGroupSellerAccountSettings';
import { EventWithData } from 'src/WebApiController';
import { ListingGroup, ListingGroupItemInput } from 'src/WebApiController';

import { MergeListingGroupInputListFields } from './components/groupingTypes';
import { flattenListingGroup } from './components/groupingUtils';

export const useFullySoldFilter = (
  events: EventWithData[],
  listingGroup?: ListingGroup
) => {
  const { data: listingGroupSettings } = useListingGroupSellerAccountSettings();
  const defaultExcludeFullySold =
    listingGroupSettings?.defaultExcludeFullySoldInGroup ?? false;

  const [userExcludeFullySoldChoice, setUserExcludeFullySoldChoice] = useState<
    boolean | null
  >(null);

  const { watch, formState } =
    useFormContext<MergeListingGroupInputListFields>();

  const mergeListingGroupInputs = watch('mergeListingGroupInputs');

  const fullySoldListingIds = useMemo(
    () =>
      events
        .flatMap((ev) =>
          (ev.entities.listings ?? []).flatMap(flattenListingGroup)
        )
        .filter((l) => l.id > 0 && l.availQty <= 0)
        .map((l) => l.id),
    [events]
  );

  const hasFullySoldListings = useMemo(() => {
    if (listingGroup) {
      return (
        (formState?.defaultValues?.mergeListingGroupInputs?.[0]
          ?.listingGroupItems || []) as ListingGroupItemInput[]
      ).some((item) => fullySoldListingIds.includes(item!.listingId!));
    }
    return !isEmpty(fullySoldListingIds);
  }, [
    formState?.defaultValues?.mergeListingGroupInputs,
    fullySoldListingIds,
    listingGroup,
  ]);

  const excludeFullySold = useMemo(() => {
    if (userExcludeFullySoldChoice !== null) {
      return userExcludeFullySoldChoice;
    }
    return defaultExcludeFullySold;
  }, [userExcludeFullySoldChoice, defaultExcludeFullySold]);

  const sanitizedEvents = useMemo(() => {
    if (excludeFullySold && hasFullySoldListings) {
      return events.map((event) => ({
        ...event,
        listings: (event.entities.listings ?? []).filter(
          ({ id }) => !fullySoldListingIds.includes(id)
        ),
      }));
    }
    return events;
  }, [excludeFullySold, hasFullySoldListings, events, fullySoldListingIds]);

  const filteredMergeListingGroupInputs = useMemo(() => {
    if (!excludeFullySold || !hasFullySoldListings) {
      return mergeListingGroupInputs;
    }
    return mergeListingGroupInputs.map((input) => ({
      ...input,
      listingGroupItems: (input.listingGroupItems || [])
        .filter(({ listingId }) => !fullySoldListingIds.includes(listingId))
        .map((item, idx) => ({
          ...item,
          priority: idx + 1,
        })),
    }));
  }, [
    excludeFullySold,
    hasFullySoldListings,
    mergeListingGroupInputs,
    fullySoldListingIds,
  ]);

  const onToggleExcludeFullySold = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setUserExcludeFullySoldChoice(e.target.checked);
    },
    []
  );

  const showExcludeFullySold = useMemo(
    () => hasFullySoldListings,
    [hasFullySoldListings]
  );

  return {
    excludeFullySold,
    showExcludeFullySold,
    mergeListingGroupInputs: filteredMergeListingGroupInputs,
    sanitizedEvents,
    onToggleExcludeFullySold,
  };
};
